import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { Navigate } from "react-router-dom";
import { Building2, ArrowRight } from "lucide-react";
import useBrand from "@/hooks/useBrand";

/**
 * Iter v34aw · Full-Bento — Login (light cream, corporate blue+emerald).
 */
export default function Login() {
  const { user, login, error } = useAuth();
  const { t, lang, toggle } = useLang();
  const brand = useBrand();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    const isClient = (user.account_type || "").toLowerCase() === "klien";
    return <Navigate to={isClient ? "/client" : "/dashboard"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email.trim(), password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#f6f5f0] flex items-center justify-center p-4 md:p-8">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left visual bento hero */}
        <div className="hidden lg:block lg:col-span-7 rounded-3xl bg-gradient-to-br from-stone-900 via-stone-800 to-blue-950 text-white p-10 relative overflow-hidden min-h-[600px]">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl"></div>
          <div className="absolute -left-16 -bottom-16 w-80 h-80 rounded-full bg-emerald-400/10 blur-3xl"></div>
          <div className="absolute right-8 bottom-8 opacity-10">
            <Building2 className="w-48 h-48"/>
          </div>
          <div className="relative h-full flex flex-col justify-between">
            <div className="flex items-center gap-3">
              {/* Iter v34cg — Logo perusahaan dari default company (fallback ke inisial "R"). */}
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center font-bold shadow-lg overflow-hidden" data-testid="login-brand-logo">
                {brand.logoUrl ? (
                  <img src={brand.logoUrl} alt="Logo" className="w-full h-full object-contain bg-white" />
                ) : (
                  <span>R</span>
                )}
              </div>
              <div>
                <div className="font-display text-lg font-semibold tracking-tight" data-testid="login-brand-name">{brand.name || "RBB ERP"}</div>
                <div className="text-[10px] text-stone-400 uppercase tracking-wider">Material · Konstruksi</div>
              </div>
            </div>
            <div className="space-y-4 max-w-lg">
              <h1 className="font-display text-5xl tracking-tight leading-tight">
                Management &amp; Katalog <span className="italic text-emerald-300">Harga</span> produk Perusahaan.
              </h1>
              <p className="text-sm text-stone-300 leading-relaxed max-w-md">
                Sebuah ruang kontrol untuk perdagangan material — dirancang untuk akurasi
                harian, lengkap dengan log sistem dan kontrol akses berbasis peran.
              </p>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-stone-500" data-testid="login-footer-brand">
              © {new Date().getFullYear()} · PT Rizal Bersaudara Berkah
            </div>
          </div>
        </div>

        {/* Right login form */}
        <div className="lg:col-span-5 rounded-3xl bg-white border border-stone-200/70 p-8 md:p-10 relative">
          <button
            onClick={toggle}
            className="absolute top-6 right-6 text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-900 border border-stone-200 hover:border-stone-400 px-3 py-1.5 rounded-full transition"
            data-testid="lang-toggle"
          >
            {lang === "id" ? "EN" : "ID"}
          </button>
          <form onSubmit={onSubmit} className="w-full max-w-sm mx-auto space-y-7 pt-8" data-testid="login-form" autoComplete="on">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-blue-700 mb-3 font-semibold">{t("welcome")}</p>
              <h2 className="font-display text-4xl tracking-tight text-stone-900">{t("login")}</h2>
              <p className="text-sm text-stone-500 mt-2">Silahkan login terlebih dahulu.</p>
            </div>

            {/* Iter v34b4 (fix #2) — Decoy anti-autofill dihapus supaya Google/browser bisa save & autofill kredensial. */}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-stone-600 mb-2">
                  {t("email")}
                </label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-stone-300 bg-white rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition"
                  data-testid="login-email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-stone-600 mb-2">
                  {t("password")}
                </label>
                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-stone-300 bg-white rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition"
                  data-testid="login-password"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-stone-900 hover:bg-blue-700 text-white py-3 px-4 rounded-full text-sm font-medium tracking-wide transition disabled:opacity-60 flex items-center justify-center gap-2 group"
              data-testid="login-submit"
            >
              {loading ? "…" : (<>{t("login")} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform"/></>)}
            </button>

            <div className="flex flex-col gap-2 text-xs pt-4 border-t border-stone-100">
              <div className="flex items-center justify-between">
                <a href="/forgot-password" className="text-stone-500 hover:text-stone-900 hover:underline" data-testid="link-forgot-password">Lupa kata sandi?</a>
                <a href="/signup" className="text-blue-700 hover:text-blue-900 hover:underline font-medium" data-testid="link-signup">Daftar Akun Klien →</a>
              </div>
              <div className="flex items-center justify-end">
                <a href="/register/karyawan" className="text-emerald-700 hover:text-emerald-900 hover:underline font-medium" data-testid="link-register-karyawan">Daftar Akun Karyawan →</a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
